import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageTitle } from "@/components/SettingsPanel";
import { VenueBookingCard, VenueAction } from "@/components/VenueBookingCard";
import { ReasonDialog } from "@/components/BookingStatus";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { Booking, User, WaTemplate } from "@/types";
import { useLive } from "@/lib/live";

type TableProps = { list: Booking[] | null; onAction?: (b: Booking, a: VenueAction) => void; templates?: WaTemplate[]; onContact?: (b: Booking, tpl: WaTemplate) => void };

export const BookingsTable = ({ list, onAction, templates, onContact }: TableProps) => {
  const { t } = usePrefs();
  if (list?.length === 0) return <p data-testid="bookings-table-empty" className="text-muted-foreground py-10 text-center">{t("b5.empty")}</p>;
  return <div className="grid gap-3 lg:grid-cols-2">{list?.map((b) => <VenueBookingCard key={b.booking_id} b={b} onAction={onAction} templates={templates} onContact={onContact} />)}</div>;
};

const TABS = [["pending", "b5.tabRequests"], ["confirmed", "b5.tabConfirmed"], ["", "b5.tabAll"]] as const;
const REASON_ACTIONS: Record<string, { path: string; title: string; required: boolean; body: (r: string) => object }> = {
  reject: { path: "reject", title: "b5.reject", required: true, body: (reason) => ({ reason }) },
  unable: { path: "unable-to-contact", title: "b5.unable", required: false, body: (note) => ({ note }) },
  cancel: { path: "cancel", title: "b5.cancelTitle", required: true, body: (reason) => ({ reason }) },
  rejectChange: { path: "change", title: "b5.rejectChange", required: true, body: (reason) => ({ approve: false, reason }) },
};

export default function VenueBookings() {
  const { t } = usePrefs();
  const me = useAuth().user as User;
  const desk = ["owner", "reception"].includes(me.venue_role || "") && me.venue_account?.status === "approved";
  const [tab, setTab] = useState<string>("pending");
  const [list, setList] = useState<Booking[] | null>(null);
  const [pending, setPending] = useState<{ b: Booking; a: string } | null>(null);
  const [templates, setTemplates] = useState<WaTemplate[]>([]);
  const load = () => api.get("/venue/bookings").then((r) => setList(r.data)).catch(() => setList([]));
  useLive(() => load());
  useEffect(() => {
    load();
    api.get("/venue/wa-templates").then((r) => setTemplates(r.data)).catch(() => {});
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const post = async (b: Booking, path: string, body: object, ok: string) => {
    try {
      await api.post(`/venue/bookings/${b.booking_id}/${path}`, body);
      toast.success(t(ok));
      setPending(null);
    } catch (e) {
      toast.error(errorText(e, t));
    }
    load();
  };
  const onContact = (b: Booking, tpl: WaTemplate) =>
    api.post(`/venue/bookings/${b.booking_id}/contact-log`, { template: tpl.title })
      .then((r) => setList((l) => l?.map((x) => (x.booking_id === b.booking_id ? r.data : x)) ?? l)).catch((e) => toast.error(errorText(e, t)));
  const onAction = (b: Booking, a: VenueAction) => {
    if (a === "confirm") return post(b, "confirm", {}, "b5.confirmed");
    if (a === "approveChange") return post(b, "change", { approve: true }, "b5.done");
    setPending({ b, a });
  };
  const shown = list?.filter((b) => !tab || b.status === tab) ?? null;
  const count = list?.filter((b) => b.status === "pending").length || 0;
  const cfg = pending && REASON_ACTIONS[pending.a];

  return (
    <div data-testid="venue-bookings-page">
      <PageTitle title={t("nav.venueBookings")} sub={count ? t("b5.pendingCount").replace("{n}", String(count)) : undefined} />
      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        {TABS.map(([k, label]) => (
          <button key={k || "all"} data-testid={`venue-bookings-tab-${k || "all"}`} onClick={() => setTab(k)} aria-pressed={tab === k}
            className={`h-10 px-5 shrink-0 rounded-full text-sm font-bold transition-[background-color,color] ${tab === k ? "bg-foreground text-background" : "bg-card border hover:bg-accent"}`}>
            {t(label)}{k === "pending" && count ? ` · ${count}` : ""}
          </button>
        ))}
      </div>
      <BookingsTable list={shown} onAction={desk ? onAction : undefined} templates={templates} onContact={desk ? onContact : undefined} />
      {pending && cfg && (
        <ReasonDialog testid={`venue-${pending.a}-dialog`} title={t(cfg.title)} required={cfg.required} onClose={() => setPending(null)}
          onSubmit={(r) => post(pending.b, cfg.path, cfg.body(r), "b5.done")} />
      )}
    </div>
  );
}
